import { useState, useEffect, useRef } from "react";
import { usePermission } from "@/auth/usePermission";
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";

interface FilterOption {
  label: string;
  value: string | number;
}

interface FilterDefinition {
  name: string; // the URL param key, e.g. "material_type"
  label: string; // "نوع متریال"
  options: FilterOption[];
}

interface DateRangeFilter {
  field: string;
  label: string;
}

interface DataTableToolbarProps {
  onSearch: (searchTerm: string) => void;
  filters?: FilterDefinition[];
  activeFilters: Record<string, string>;
  onFilterChange: (filterName: string, value: string) => void;
  onExportExcel?: () => void;
  onExportPDF?: () => void;
  isExporting?: boolean;
  exportPermission?: string;
  dateRangeFilters?: DateRangeFilter[];
}

export function DataTableToolbar({
  onSearch,
  filters = [],
  activeFilters,
  onFilterChange,
  onExportExcel,
  onExportPDF,
  isExporting = false,
  exportPermission,
  dateRangeFilters = [],
}: DataTableToolbarProps) {
  const [searchTerm, setSearchTerm] = useState("");
  // If exportPermission is provided, check it, otherwise default to true (since page is already protected)
  const hasPerm = usePermission(exportPermission || "");
  const canExport = exportPermission ? hasPerm : true;

  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      onSearchRef.current(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const inputStyle = {
    padding: "0.5rem 0.8rem",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid var(--glass-border)",
    borderRadius: "8px",
    color: "var(--color-text)",
    fontSize: "0.9rem",
    minWidth: "200px",
  };

  const selectStyle = {
    ...inputStyle,
    background: "rgba(30,30,40,0.9)",
    minWidth: "160px",
  };

  return (
    <div
      className="glass-panel mb-4 p-4 flex flex-wrap items-center justify-between"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "1rem",
        alignItems: "center",
        justifyContent: "space-between",
        border: "1px solid var(--glass-border)",
        background: "rgba(255,255,255,0.03)",
        borderRadius: "8px",
      }}
    >
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", flex: 1 }}>
        {/* Search Input */}
        <div style={{ position: "relative" }}>
          <input
            type="text"
            placeholder="جستجو..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={inputStyle}
          />
          {searchTerm && (
            <button
              onClick={() => { setSearchTerm(""); onSearch(""); }}
              style={{
                position: "absolute",
                left: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                color: "var(--color-text-muted)",
                cursor: "pointer",
                padding: "2px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="پاک کردن"
            >
              ✕
            </button>
          )}
        </div>

        {/* Dynamic Filters */}
        {filters.map((filter) => (
          <div key={filter.name}>
            <select
              value={activeFilters[filter.name] || ""}
              onChange={(e) => onFilterChange(filter.name, e.target.value)}
              style={selectStyle}
            >
              <option value="">{filter.label} (همه)</option>
              {filter.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ))}

        {/* Dynamic Date Range Filters */}
        {dateRangeFilters.map((drf) => {
          return (
            <DateRangeFilterGroup
              key={drf.field}
              drf={drf}
              activeFilters={activeFilters}
              onFilterChange={onFilterChange}
            />
          );
        })}
      </div>

      {/* Actions (Export / Print) */}
      {canExport && (onExportExcel || onExportPDF) && (
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {onExportExcel && (
            <button
              className="btn btn-secondary"
              onClick={onExportExcel}
              disabled={isExporting}
              style={{ padding: "0.5rem 1rem", fontSize: "0.9rem" }}
            >
              {isExporting ? "در حال دریافت..." : "خروجی Excel"}
            </button>
          )}
          {onExportPDF && (
            <button
              className="btn btn-secondary"
              onClick={onExportPDF}
              disabled={isExporting}
              style={{ padding: "0.5rem 1rem", fontSize: "0.9rem" }}
            >
              {isExporting ? "در حال دریافت..." : "خروجی PDF"}
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => window.print()}
            style={{ padding: "0.5rem 1rem", fontSize: "0.9rem" }}
          >
            چاپ 🖨️
          </button>
        </div>
      )}
    </div>
  );
}

function DateRangeFilterGroup({ drf, activeFilters, onFilterChange }: any) {
  const afterKey = `${drf.field}_after`;
  const beforeKey = `${drf.field}_before`;
  
  const afterVal = activeFilters[afterKey] ? new Date(activeFilters[afterKey]) : null;
  const beforeVal = activeFilters[beforeKey] ? new Date(activeFilters[beforeKey]) : null;

  const afterRef = useRef<any>(null);
  const beforeRef = useRef<any>(null);

  const clearBtnStyle = {
    position: "absolute" as const,
    left: "6px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    color: "var(--color-text-muted)",
    cursor: "pointer",
    padding: "2px",
    zIndex: 2,
  };

  const todayBtnBlock = (key: string, ref: any) => (
    <div style={{ padding: "8px", borderTop: "1px solid var(--glass-border)", textAlign: "center" }}>
      <button
        type="button"
        style={{ background: "transparent", border: "none", color: "var(--color-primary)", cursor: "pointer", fontSize: "0.85rem", fontWeight: "bold" }}
        onClick={() => {
          onFilterChange(key, new Date().toISOString());
          ref.current?.closeCalendar();
        }}
      >
        امروز
      </button>
    </div>
  );

  return (
    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
      <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>{drf.label} از:</span>
      <div style={{ position: "relative" }}>
        <DatePicker
          ref={afterRef}
          calendar={persian}
          locale={persian_fa}
          value={afterVal}
          onChange={(date: DateObject | null) => {
            onFilterChange(afterKey, date ? date.toDate().toISOString() : "");
          }}
          inputClass="input-field"
          style={{ height: "38px", width: "130px" }}
        >
          {todayBtnBlock(afterKey, afterRef)}
        </DatePicker>
        {afterVal && (
          <button onClick={() => onFilterChange(afterKey, "")} style={clearBtnStyle} title="پاک کردن">✕</button>
        )}
      </div>

      <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>تا:</span>
      <div style={{ position: "relative" }}>
        <DatePicker
          ref={beforeRef}
          calendar={persian}
          locale={persian_fa}
          value={beforeVal}
          onChange={(date: DateObject | null) => {
            onFilterChange(beforeKey, date ? date.toDate().toISOString() : "");
          }}
          inputClass="input-field"
          style={{ height: "38px", width: "130px" }}
        >
          {todayBtnBlock(beforeKey, beforeRef)}
        </DatePicker>
        {beforeVal && (
          <button onClick={() => onFilterChange(beforeKey, "")} style={clearBtnStyle} title="پاک کردن">✕</button>
        )}
      </div>
    </div>
  );
}
